import { useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, AlertTriangle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function AdminMaintenance() {
  const [confirm, setConfirm] = useState("");
  const [keepEmails, setKeepEmails] = useState("demo@creativeops.io, admin@creativeops.io");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const purge = async () => {
    if (confirm !== "PURGE") { toast.error('Type "PURGE" exactly to confirm.'); return; }
    const ok = window.confirm(
      "Final warning — this will permanently delete every workspace, project, lead, invoice and uploaded file " +
      "outside the preserved owner accounts. Continue?"
    );
    if (!ok) return;
    setBusy(true);
    try {
      const keep_emails = keepEmails.split(",").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.post("/admin/system/purge", { confirm, keep_emails });
      setResult(data);
      setConfirm("");
      toast.success(`Purge complete. Kept ${data.kept_workspaces} workspace(s).`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Purge failed"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader
        eyebrow="Super Admin"
        title="System Maintenance"
        description="Dangerous, destructive operations live here. Run them only when you really mean to."
        testid="admin-maintenance-header"
      />
      <div className="px-4 sm:px-6 md:px-10 py-6 space-y-6 max-w-3xl">
        <div className="border-2 border-destructive/40 p-6 bg-destructive/5" data-testid="purge-panel">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center shrink-0">
              <ShieldAlert className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <div className="label-eyebrow text-destructive mb-1">Danger zone</div>
              <h2 className="font-bold text-xl mb-1" style={{ fontFamily: "var(--font-heading)" }}>Purge non-essential data</h2>
              <p className="text-sm text-foreground/70">
                Deletes <strong>all</strong> workspaces, projects, leads, invoices, attachments, calendars, vault credentials,
                announcements, SOPs and user accounts <strong>outside the preserved owner allow-list</strong>.
                Master data (plans, legal pages, branding, email config, super admins, system settings) is left untouched.
              </p>
            </div>
          </div>

          <div className="space-y-4 mt-6">
            <div>
              <Label className="label-eyebrow text-[10px] block mb-1">Preserved owner emails (comma-separated)</Label>
              <Input
                value={keepEmails}
                onChange={(e) => setKeepEmails(e.target.value)}
                className="h-10"
                data-testid="purge-keep-emails-input"
              />
              <p className="text-[10px] text-foreground/50 mt-1">
                Super admin accounts are always preserved. Workspaces owned by these emails (and their owners) survive the purge.
              </p>
            </div>

            <div>
              <Label className="label-eyebrow text-[10px] block mb-1">Type <code className="font-mono text-destructive">PURGE</code> to confirm</Label>
              <Input
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder='PURGE'
                className="h-10 font-mono"
                data-testid="purge-confirm-input"
              />
            </div>

            <Button
              type="button"
              variant="destructive"
              className="rounded-none"
              onClick={purge}
              disabled={busy || confirm !== "PURGE"}
              data-testid="purge-execute-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> {busy ? "Purging…" : "Purge data"}
            </Button>
          </div>
        </div>

        {result && (
          <div className="border border-border p-5" data-testid="purge-result">
            <div className="flex items-start gap-3 mb-3">
              <AlertTriangle className="w-5 h-5 text-foreground/60 mt-0.5" />
              <div>
                <div className="font-semibold text-sm">Purge result</div>
                <div className="text-xs text-foreground/60">Kept {result.kept_users} user(s), {result.kept_workspaces} workspace(s)</div>
              </div>
            </div>
            <pre className="text-[11px] bg-muted/40 p-3 overflow-auto max-h-72" data-testid="purge-result-json">
              {JSON.stringify(result.deleted, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </>
  );
}
